import { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Cropper, { type Area } from 'react-easy-crop';
import { getCroppedImageBlob, type CropArea } from '../../utils/cropImage';

interface AvatarCropDialogProps {
  isOpen: boolean;
  imageUrl: string;
  onComplete: (blob: Blob) => void;
  onCancel: () => void;
}

export default function AvatarCropDialog({
  isOpen,
  imageUrl,
  onComplete,
  onCancel,
}: AvatarCropDialogProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(
    null,
  );
  const [processing, setProcessing] = useState(false);

  // Lock body scroll when modal is open to prevent background scroll/chaos
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isOpen]);

  const onCropAreaChange = useCallback((_croppedArea: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleUsePhoto = useCallback(async () => {
    if (!croppedAreaPixels) return;
    setProcessing(true);
    try {
      const blob = await getCroppedImageBlob(imageUrl, croppedAreaPixels, true);
      onComplete(blob);
    } catch (err) {
      console.error('Crop failed:', err);
    } finally {
      setProcessing(false);
    }
  }, [imageUrl, croppedAreaPixels, onComplete]);

  if (!isOpen) return null;

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="crop-dialog-title"
    >
      {/* Strong backdrop - fully dims background to avoid visual chaos */}
      <div
        className="absolute inset-0 bg-black/85 backdrop-blur-md"
        onClick={onCancel}
        aria-hidden
      />
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh] ring-2 ring-white/20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 shrink-0">
          <h3
            id="crop-dialog-title"
            className="text-lg font-semibold text-gray-900"
          >
            Crop profile photo
          </h3>
          <button
            type="button"
            onClick={onCancel}
            className="p-1.5 rounded-full hover:bg-gray-200 text-gray-600 transition-colors"
            aria-label="Cancel"
          >
            <span className="text-xl leading-none">&times;</span>
          </button>
        </div>
        <div className="relative h-[320px] w-full bg-gray-900">
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onCropAreaChange={onCropAreaChange}
            onZoomChange={setZoom}
            style={{
              containerStyle: { background: '#111' },
            }}
          />
        </div>
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 flex-1 max-w-[200px]">
            <span className="text-sm text-gray-600">Zoom</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gray-200 accent-herbal-green"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUsePhoto}
              disabled={processing || !croppedAreaPixels}
              className="px-4 py-2 rounded-xl bg-herbal-green text-white hover:bg-herbal-green-dark disabled:opacity-50 transition-colors"
            >
              {processing ? 'Processing…' : 'Use photo'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
